import { xatSmilieResultImgHtml } from '@/lib/xat-smilie';

export type ContentBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; level: 2 | 3; text: string }
  | { type: 'steps'; items: { title: string; description: string }[] }
  | { type: 'list'; items: string[] }
  | { type: 'info'; title: string; content: string }
  | { type: 'tip'; title: string; content: string }
  | { type: 'warning'; title: string; content: string }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'divider' }
  /** Bloco estilo consola — HTML, CSS, PHP, etc. (texto puro, sem execução). */
  | { type: 'code'; language: string; code: string };

export type ArticleContent = {
  intro: string;
  blocks: ContentBlock[];
};

export const articleContent: Record<string, Record<number, ArticleContent>> = {
  'primeiros-passos': {
    1: {
      intro:
        'Enviar uma mensagem no xat é tão simples quanto digitar e pressionar Enter. Mas tem muito mais que você pode fazer para deixar suas conversas mais expressivas e organizadas.',
      blocks: [
        { type: 'heading', level: 2, text: 'Como enviar uma mensagem' },
        {
          type: 'paragraph',
          text: 'Digite o que você quer dizer na caixa de texto na parte inferior do chat e pressione Enter (ou Return). Sua mensagem aparecerá imediatamente para todos os participantes da sala.',
        },
        {
          type: 'tip',
          title: 'Dica rápida',
          content:
            'Pressione a seta para cima ↑ para editar rapidamente a última mensagem que você enviou, sem precisar redigitar tudo.',
        },
        { type: 'heading', level: 2, text: 'Formatando o texto' },
        {
          type: 'paragraph',
          text: 'O xat suporta formatação de texto chamada Markdown. Você pode deixar palavras em negrito, itálico, tachado ou até inserir links clicáveis. Para formatar, selecione o texto na caixa de mensagem, clique com o botão direito e escolha o estilo desejado no menu de contexto. Ou use os atalhos de teclado:',
        },
        {
          type: 'table',
          headers: ['Estilo', 'Como usar', 'Exemplo', 'Atalho'],
          rows: [
            ['Negrito', 'Envolva com *asteriscos*', '*Olá, como vai?*', 'Ctrl + B'],
            ['Itálico', 'Envolva com _underscores_', 'Você _tem certeza_?', 'Ctrl + I'],
            ['Tachado', 'Envolva com ~tils~', 'Eu ~amo~ odeio isso!', 'Ctrl + U'],
            ['Citação', 'Comece com >[ e termine com ]', '>[texto citado] meu reply', 'Ctrl + Q'],
            ['Link', 'Texto em [] e URL em () logo após', 'Visite [xat](https://xat.com)!', '—'],
          ],
        },
        {
          type: 'info',
          title: 'Combinando estilos',
          content:
            'Você pode misturar formatos ao mesmo tempo! Para texto em negrito e itálico juntos, use *_assim_*.',
        },
        { type: 'heading', level: 2, text: 'Atalhos de teclado do chat' },
        {
          type: 'paragraph',
          text: 'O xat tem atalhos que agilizam muito o dia a dia — vale a pena decorar:',
        },
        {
          type: 'table',
          headers: ['Tecla', 'Ação'],
          rows: [
            ['Esc', 'Fecha a aba de chat privado atual e vai para a aba à esquerda'],
            ['← →', 'Navega entre as abas abertas (com a caixa de texto vazia)'],
            ['↑', 'Edita a última mensagem enviada'],
            ['Ctrl + clique (link YouTube)', 'Abre o vídeo em uma nova aba'],
          ],
        },
      ],
    },

    2: {
      intro:
        'No xat, as mensagens não são definitivas. Você pode editar o que disse, citar uma resposta específica ou reagir com smilies — tudo de forma intuitiva.',
      blocks: [
        { type: 'heading', level: 2, text: 'Editando uma mensagem' },
        {
          type: 'paragraph',
          text: 'Mandou uma mensagem com erro de digitação? Clique com o botão direito na mensagem e escolha "Editar". Ela voltará para a caixa de texto para você corrigir. Quando terminar, pressione Enter para salvar.',
        },
        {
          type: 'info',
          title: 'Regras de edição',
          content:
            'Você tem até 2 minutos após o envio para editar. A mensagem editada exibe uma marcação "Editado" visível para todos. Uma mensagem só pode ser editada uma vez — e não pode conter palavras impróprias.',
        },
        {
          type: 'warning',
          title: 'Requer conta registrada',
          content:
            'A edição de mensagens está disponível apenas para usuários com conta registrada no xat.',
        },
        { type: 'heading', level: 2, text: 'Citando uma mensagem' },
        {
          type: 'paragraph',
          text: 'Quer responder algo específico que alguém disse? Clique com o botão direito na mensagem e escolha "Citar". O texto será colocado na sua caixa de entrada com formatação de citação. Escreva sua resposta logo depois e envie.',
        },
        {
          type: 'tip',
          title: 'Citação manual',
          content:
            'Use Ctrl+Q para inserir a formatação de citação manualmente. Comece com >[ e termine com ] para envolver o texto citado.',
        },
        { type: 'heading', level: 2, text: 'Traduzindo mensagens' },
        {
          type: 'paragraph',
          text: 'Alguém escreveu em outro idioma? Clique com o botão direito na mensagem e escolha "Traduzir". O xat traduz o texto para o idioma configurado na sua conta. Para ver o original novamente, clique na mensagem traduzida.',
        },
        {
          type: 'info',
          title: 'Tradução rápida vs. tradução automática',
          content:
            'A tradução manual (clique direito) traduz uma mensagem específica só para você. A tradução automática nas Configurações da Conta traduz tudo de forma contínua para todos.',
        },
        {
          type: 'warning',
          title: 'Recurso para assinantes',
          content: 'A tradução manual de mensagens individuais está disponível somente para assinantes do xat.',
        },
        { type: 'heading', level: 2, text: 'Deletando mensagens' },
        {
          type: 'paragraph',
          text: 'Clique com o botão direito em qualquer mensagem e escolha "Deletar". A mensagem desaparece imediatamente para todos na sala.',
        },
        {
          type: 'warning',
          title: 'Permissão necessária',
          content:
            'Por padrão, apenas Moderadores Temporários ou cargos superiores podem deletar mensagens de outros usuários.',
        },
        { type: 'heading', level: 2, text: 'Reagindo a mensagens' },
        {
          type: 'paragraph',
          text: 'Assim como em redes sociais, você pode reagir a mensagens com smilies. Clique com o botão direito em qualquer mensagem e escolha "Reagir". O seletor de reações abre com centenas de smilies — tem até uma barra de busca para encontrar o perfeito.',
        },
        {
          type: 'list',
          items: [
            'Uma mensagem suporta até 7 reações diferentes ao mesmo tempo',
            'No chat principal, você só pode reagir uma vez por mensagem',
            'Em chats privados, pode reagir quantas vezes quiser',
            'Você não pode reagir às suas próprias mensagens',
            'Clique em "Quem reagiu" para ver quem colocou cada reação e há quanto tempo',
          ],
        },
        {
          type: 'info',
          title: 'Desativando reações',
          content:
            'Não quer ver reações? Desative-as em Configurações da Conta > Aparência > Reações.',
        },
      ],
    },

    3: {
      intro:
        'Os smilies são o coração do xat. São muito mais do que emojis comuns — são uma identidade, uma linguagem, e existem literalmente milhares deles. Veja como usar e descobrir os melhores.',
      blocks: [
        { type: 'heading', level: 2, text: 'O que são smilies?' },
        {
          type: 'paragraph',
          text: 'Smilies são os emoticons do xat. Você pode adicioná-los digitando o código entre parênteses, como (smile) ou (cool), ou clicando diretamente neles na barra acima da caixa de mensagem.',
        },
        { type: 'heading', level: 2, text: 'Abrindo a lista completa' },
        {
          type: 'paragraph',
          text: 'Viu a bola 8 mágica na barra de smilies? Clique nela para abrir o inventário completo, organizado por categorias. Você verá apenas os smilies gratuitos e os que fazem parte dos powers que você já possui.',
        },
        {
          type: 'tip',
          title: 'Smilies gratuitos',
          content:
            'Tem uma lista enorme de smilies disponíveis para qualquer usuário, sem custo nenhum. Consulte a página de smilies gratuitos na wiki do xat para conferir todos.',
        },
        { type: 'heading', level: 2, text: 'Atalhos de texto rápidos' },
        {
          type: 'paragraph',
          text: 'Vários smilies populares têm atalhos que você já conhece de outros apps. Estes funcionam diretamente na caixa de mensagem:',
        },
        {
          type: 'table',
          headers: ['Digite', 'Vira', 'RESULTADO'],
          rows: [
            [':) ou :-)', '(smile)', xatSmilieResultImgHtml('(smile)')],
            [':d', '(biggrin)', xatSmilieResultImgHtml('(biggrin)')],
            [';) ou ;-)', '(wink)', xatSmilieResultImgHtml('(wink)')],
            [':o ou :-o', '(eek)', xatSmilieResultImgHtml('(eek)')],
            [':p', '(tongue)', xatSmilieResultImgHtml('(tongue)')],
            ['8-)', '(cool)', xatSmilieResultImgHtml('(cool)')],
            [':@', '(mad)', xatSmilieResultImgHtml('(mad)')],
            [':s', '(confused)', xatSmilieResultImgHtml('(confused)')],
            [':( ou :-(', '(frown)', xatSmilieResultImgHtml('(frown)')],
            [":'(", '(cry2)', xatSmilieResultImgHtml('(cry2)')],
            ['|-)', '(sleepy)', xatSmilieResultImgHtml('(sleepy)')],
            [':-*', '(kiss)', xatSmilieResultImgHtml('(kiss)')],
          ],
        },
        { type: 'heading', level: 2, text: 'Autocompletar smilies' },
        {
          type: 'paragraph',
          text: 'O jeito mais rápido de encontrar um smiley pelo nome é pelo autocompletar. Digite ( na caixa de mensagem e comece a escrever o nome do smiley. Uma lista de sugestões aparece instantaneamente e se afina conforme você digita.',
        },
        {
          type: 'steps',
          items: [
            {
              title: 'Abra o parêntese',
              description: 'Digite ( na caixa de mensagem para ativar o autocompletar.',
            },
            {
              title: 'Escreva as primeiras letras',
              description:
                'Ex: (cool vai sugerir (cool), (reCOOL) e semelhantes. Funciona com maiúsculas também.',
            },
            {
              title: 'Selecione o smiley',
              description:
                'Clique na sugestão ou navegue com as setas do teclado e pressione Enter para inserir.',
            },
          ],
        },
        {
          type: 'info',
          title: 'Busca por palavras inteiras',
          content:
            'O autocompletar aceita buscas por palavras completas. Digite "legal" e ele encontra "legal", "reLEGAL" e variações. Funciona para usuários registrados e não registrados.',
        },
      ],
    },

    4: {
      intro:
        'Além dos smilies, o xat tem GIFs animados do GIPHY e efeitos sonoros chamados Audies. São ótimas formas de animar qualquer conversa com algo visual ou sonoro.',
      blocks: [
        { type: 'heading', level: 2, text: 'GIFs no chat' },
        {
          type: 'paragraph',
          text: 'O xat é integrado com o GIPHY, o maior banco de GIFs do mundo. Para usar, clique em "GIFs" na Quickbar (a barra lateral que aparece quando você clica na seta no lado direito do chat).',
        },
        {
          type: 'steps',
          items: [
            {
              title: 'Abra a Quickbar',
              description: 'Clique na seta no lado direito do chat para exibir o menu da Quickbar.',
            },
            {
              title: 'Clique em "GIFs"',
              description: 'O buscador abrirá com resultados populares já carregados.',
            },
            {
              title: 'Pesquise e envie',
              description:
                'Digite o que você quer (ex: "risada", "dança", "gato") e clique no GIF para enviá-lo no chat.',
            },
          ],
        },
        {
          type: 'tip',
          title: 'GIFs favoritos',
          content:
            'Passe o mouse sobre um GIF e clique na estrela que aparece no canto superior direito para salvá-lo nos favoritos. Acesse sua lista de favoritos clicando na estrela acima da lista de GIFs.',
        },
        { type: 'heading', level: 2, text: 'Configurando como os GIFs aparecem' },
        {
          type: 'paragraph',
          text: 'Vá em Configurações > Aparência > GIFs para controlar o comportamento dos GIFs no seu chat:',
        },
        {
          type: 'list',
          items: [
            'Ativado — exibe e anima os GIFs normalmente (padrão)',
            'Sem animação — mostra a imagem parada; só anima ao clicar',
            'Reproduzir ao passar o mouse — anima apenas quando o cursor passa por cima',
            'Desativado — exibe apenas o link no lugar da imagem',
          ],
        },
        {
          type: 'info',
          title: 'Filtragem automática',
          content:
            'O xat filtra GIFs impróprios automaticamente com base na classificação do GIPHY. Se um GIF for bloqueado, aparecerá como "[GIF link removido]" para todos.',
        },
        {
          type: 'warning',
          title: 'Requer conta registrada',
          content: 'O envio de GIFs no chat está disponível apenas para usuários registrados.',
        },
        { type: 'heading', level: 2, text: 'Audies — efeitos sonoros' },
        {
          type: 'paragraph',
          text: 'Audies são efeitos de som que aparecem nas mensagens como um ícone de áudio clicável. Para enviar um audie, use o símbolo # seguido do nome do som na caixa de mensagem. Exemplos: #raspberry, #giddy, #ohh. Quem quiser ouvir clica no ícone.',
        },
        {
          type: 'tip',
          title: 'Use com moderação',
          content:
            'Spammar audies pode irritar outros usuários e resultar em ban em alguns chats. Use-os para complementar a conversa, não como interrupção constante.',
        },
        {
          type: 'info',
          title: 'Quem não ouve audies',
          content:
            'Usuários com o som do chatbox no mudo ou que possuem o power Noaudies ativado não ouvem os sons — mas ainda verão o ícone na mensagem.',
        },
      ],
    },

    5: {
      intro:
        'No xat, existe uma diferença importante entre Mensagem Privada e Chat Privado. Entender os dois vai mudar como você se comunica com pessoas específicas.',
      blocks: [
        { type: 'heading', level: 2, text: 'Qual a diferença?' },
        {
          type: 'paragraph',
          text: 'A Mensagem Privada (MP) é um único envio no chat principal que só o destinatário vê — como um sussurro numa sala cheia. O Chat Privado (CP) abre uma conversa separada em nova aba, exclusiva entre vocês dois — como sair da sala e encontrar alguém num corredor.',
        },
        {
          type: 'table',
          headers: ['', 'Mensagem Privada', 'Chat Privado'],
          rows: [
            ['Onde aparece', 'No chat principal, com um cadeado', 'Em uma aba separada'],
            ['Duração', 'Somente aquela mensagem', 'Conversa contínua'],
            ['Para responder', 'Precisa configurar o cadeado novamente', 'Aba de conversa aberta'],
            ['Ideal para', 'Avisos rápidos e discretos', 'Conversas longas e privadas'],
          ],
        },
        { type: 'heading', level: 2, text: 'Enviando uma Mensagem Privada' },
        {
          type: 'steps',
          items: [
            {
              title: 'Clique no nome do usuário',
              description: 'Na lista de visitantes ou em uma mensagem que ele enviou.',
            },
            {
              title: 'Selecione "Mensagem Privada"',
              description:
                'Um cadeado grande aparecerá no lado esquerdo da caixa de texto, indicando que o próximo envio será privado.',
            },
            {
              title: 'Escreva e envie',
              description:
                'Digite a mensagem e pressione Enter. Ela aparece com cadeado dourado, visível só para você e o destinatário.',
            },
          ],
        },
        {
          type: 'tip',
          title: 'Confirmando o destinatário',
          content:
            'Passe o mouse sobre o cadeado para ver para quem a mensagem será enviada. Clique no X para cancelar antes de enviar.',
        },
        { type: 'heading', level: 2, text: 'Iniciando um Chat Privado' },
        {
          type: 'steps',
          items: [
            {
              title: 'Clique no nome do usuário',
              description: 'Na lista de visitantes ou em uma mensagem.',
            },
            {
              title: 'Escolha "Chat Privado"',
              description: 'Uma nova aba aparece na parte inferior com o nome da pessoa.',
            },
            {
              title: 'Converse',
              description:
                'Tudo o que você escrever nessa aba só chegará para aquela pessoa. A aba pisca em verde quando uma mensagem nova chega.',
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'Para encerrar, passe o mouse sobre a aba e clique no X vermelho. Usar Esc também fecha a aba ativa e vai para a aba à esquerda.',
        },
        { type: 'heading', level: 2, text: 'Saída automática por inatividade' },
        {
          type: 'warning',
          title: 'Desconexão automática',
          content:
            'Se você ficar mais de 15 minutos sem enviar mensagens, o chat te desconecta automaticamente. Pressione "Entrar" no canto inferior direito para voltar. Donos e moderadores não são desconectados.',
        },
      ],
    },

    6: {
      intro:
        'A lista de visitantes é o painel lateral do chat que mostra quem está presente na sala. Cada usuário tem uma "peça" colorida que indica seu cargo e status. Entender isso facilita muito a vida no xat.',
      blocks: [
        { type: 'heading', level: 2, text: 'O que é a lista de visitantes?' },
        {
          type: 'paragraph',
          text: 'É a coluna lateral do chat listando todos os usuários ativos na sala. Cada nome vem acompanhado de uma "peça" (pawn) colorida, que indica o nível daquele usuário naquele chat.',
        },
        { type: 'heading', level: 2, text: 'O que cada cor significa?' },
        {
          type: 'list',
          items: [
            'Branca — visitante (não registrado ou sem rank especial)',
            'Amarela — membro (adicionado pelo dono como membro do chat)',
            'Vermelha — moderador (pode banir, kickar e gerenciar o chat)',
            'Azul — dono (controle avançado sobre o grupo)',
            'Roxa — dono principal (criador do chat, controle total)',
            'Verde pulsando — usuário está digitando ou acabou de enviar uma mensagem',
          ],
        },
        {
          type: 'info',
          title: 'Status de disponibilidade',
          content:
            'Quando um usuário aparece como "disponível", ele pode estar presente na sala mas não necessariamente olhando o chat. A presença na lista não garante resposta imediata.',
        },
        { type: 'heading', level: 2, text: 'Interagindo com outros usuários' },
        {
          type: 'paragraph',
          text: 'Clicar no nome de qualquer pessoa na lista abre um menu de interação com diversas opções: enviar mensagem privada, iniciar chat privado, adicionar como amigo, ignorar ou bloquear.',
        },
        { type: 'heading', level: 2, text: 'Grupos com abas' },
        {
          type: 'paragraph',
          text: 'Alguns chats exibem outras salas como abas extras no canto inferior esquerdo. Donos fazem isso para dar suporte a grupos parceiros. Ao clicar na aba de outro grupo, você é desconectado do chat atual e conectado ao novo.',
        },
        {
          type: 'tip',
          title: 'Descobrir mais salas',
          content:
            'Quer encontrar outros chats públicos? Acesse xat.com/#groups para ver a lista de grupos ativos no xat.',
        },
      ],
    },

    7: {
      intro:
        'Nem toda interação no xat é positiva. O xat oferece ferramentas completas para gerenciar quem você quer ouvir — desde adicionar amigos até silenciar ou bloquear quem te incomoda.',
      blocks: [
        { type: 'heading', level: 2, text: 'Adicionando amigos' },
        {
          type: 'paragraph',
          text: 'Adicionar alguém como amigo permite acompanhar quando essa pessoa está online, em qual chat ela está, e facilita o contato via mensagem privada. O amigo aparece em negrito e sobe para o topo do seu rank na lista.',
        },
        {
          type: 'steps',
          items: [
            {
              title: 'Clique no nome da pessoa',
              description: 'Na lista de visitantes ou em uma mensagem dela no chat.',
            },
            {
              title: 'Escolha "Adicionar como amigo"',
              description: 'A pessoa aparecerá na sua aba de Amigos, na parte inferior da lista.',
            },
            {
              title: 'Acompanhe seus amigos',
              description:
                'Na aba de Amigos, veja quais deles estão online em qualquer chat — não só no atual.',
            },
          ],
        },
        {
          type: 'tip',
          title: 'Adicionar por ID',
          content:
            'Sabe o ID numérico de alguém? Use o comando /f143742 Kyle no chat para adicionar o ID 143742 com o apelido "Kyle". Para remover, use /f-143742.',
        },
        { type: 'heading', level: 2, text: 'Ignorando um usuário' },
        {
          type: 'paragraph',
          text: 'Ignorar faz as mensagens de um usuário sumirem da sua tela. A peça dele fica cinza na lista de visitantes. Ele continua vendo o que você escreve, mas você não vê nada do que ele manda — mensagens, chats privados ou mensagens privadas.',
        },
        {
          type: 'steps',
          items: [
            {
              title: 'Clique no nome do usuário',
              description: 'Na lista ou em uma mensagem.',
            },
            {
              title: 'Clique em "Ignorar"',
              description: 'A peça da pessoa ficará cinza na lista de visitantes.',
            },
            {
              title: 'Para desfazer',
              description: 'Abra a Quickbar > Mais > Ignorados e clique no X ao lado do ID.',
            },
          ],
        },
        { type: 'heading', level: 2, text: 'Bloqueando um usuário' },
        {
          type: 'paragraph',
          text: 'Bloquear vai além de ignorar. A pessoa não consegue mais te enviar mensagem privada, iniciar chat privado, se casar ou enviar presentes para você. A peça dela some completamente da sua lista de visitantes.',
        },
        {
          type: 'steps',
          items: [
            {
              title: 'Clique no nome do usuário',
              description: 'Na lista ou em uma mensagem.',
            },
            {
              title: 'Clique no ícone de bloqueio (⊘)',
              description: 'Fica no canto superior direito do menu de interação.',
            },
            {
              title: 'Confirme o bloqueio',
              description: 'A pessoa será adicionada à sua lista de bloqueados.',
            },
            {
              title: 'Para desbloquear',
              description: 'Abra a Quickbar > Mais > Bloqueados e clique no X ao lado do ID.',
            },
          ],
        },
        { type: 'heading', level: 2, text: 'Ignorar vs. Bloquear: qual a diferença?' },
        {
          type: 'table',
          headers: ['Função', 'Ignorar', 'Bloquear'],
          rows: [
            ['Você não vê as mensagens deles', '✓', '✓'],
            ['A peça some da lista de visitantes', '✗', '✓'],
            ['Removido da sua lista de amigos', '✗', '✓'],
            ['Não pode te enviar MP ou CP', '✗', '✓'],
            ['Não pode te enviar presentes ou transferências', '✗', '✓'],
            ['Não vê seu perfil, avatar e status de amigo', '✗', '✓'],
          ],
        },
        {
          type: 'info',
          title: 'Para moderadores e donos',
          content:
            'Mesmo ignorando ou bloqueando alguém, moderadores e donos ainda veem as mensagens deles no chat principal para fins de moderação. Segure Shift antes de ignorar para ter o mesmo efeito que um membro comum.',
        },
      ],
    },

    8: {
      intro:
        'Seu perfil no xat é o que as outras pessoas veem quando clicam no seu nome. Personalizá-lo com um nome, avatar e status únicos é o primeiro passo para criar sua identidade na plataforma.',
      blocks: [
        { type: 'heading', level: 2, text: 'Abrindo seu perfil' },
        {
          type: 'paragraph',
          text: 'Clique no seu próprio nome no topo da lista de visitantes. Uma janela de perfil vai abrir com todos os campos editáveis. Se você for novo, provavelmente está com um nome aleatório tipo "WuggyBunch" — mas isso muda agora.',
        },
        { type: 'heading', level: 2, text: 'Mudando seu nome' },
        {
          type: 'paragraph',
          text: 'Na janela de perfil, localize o campo "Nome" e escreva como você quer ser chamado. Você pode usar letras, números e até códigos de smilies no nome — eles aparecem diretamente na lista de visitantes e de amigos.',
        },
        {
          type: 'tip',
          title: 'Smilies no nome',
          content:
            'Coloque smilies no nome de chat usando o código entre parênteses, como (smile). Eles aparecerão ao lado do seu nome na lista de visitantes.',
        },
        { type: 'heading', level: 2, text: 'Trocando seu avatar' },
        {
          type: 'paragraph',
          text: 'O avatar é a imagem exibida ao lado do seu nome no chat. Você tem dois caminhos:',
        },
        {
          type: 'list',
          items: [
            'Avatar padrão: Copie o número de um dos avatares da galeria oficial e cole no campo "Avatar".',
            'Avatar personalizado: Faça upload de qualquer imagem em img.xatblog.net, copie o link direto e cole no campo "Avatar".',
          ],
        },
        { type: 'heading', level: 2, text: 'Adicionando uma página inicial' },
        {
          type: 'paragraph',
          text: 'A "Página Inicial" é um link que aparece no seu perfil quando alguém clica no seu nome. Pode ser seu site, seu xatspace ou qualquer URL. Para adicionar, cole o link no campo correspondente e clique em "Salvar". Para remover, deixe o campo em branco.',
        },
        { type: 'heading', level: 2, text: 'Resetando seu ID temporário' },
        {
          type: 'paragraph',
          text: 'Usuários não registrados recebem um ID temporário ("toon"). Para gerar um novo, vá em "conta" no canto superior direito e clique em "sair". Um novo ID será criado ao acessar qualquer chat.',
        },
        {
          type: 'warning',
          title: 'Cuidado ao resetar',
          content:
            'Resetar o ID pode apagar seu status, powers ativados e outras configurações pessoais. Se não quiser perder esses dados, registre sua conta em vez de resetar.',
        },
      ],
    },

    9: {
      intro:
        'As configurações do xat dão controle total sobre como o chat funciona para você. Do modo noturno às notificações, é aqui que você deixa tudo do seu jeito.',
      blocks: [
        { type: 'heading', level: 2, text: 'Como acessar as configurações' },
        {
          type: 'paragraph',
          text: 'Existem dois caminhos: clique no seu nome na lista de visitantes e depois em "Configurações", ou abra a Quickbar (seta no lado direito do chat) e clique em "Configurações".',
        },
        { type: 'heading', level: 2, text: 'Configurações gerais' },
        {
          type: 'list',
          items: [
            'Entrar automaticamente — define se você entra no chat sozinho ao abrir a página.',
            'Modo Stealth — aparece como visitante mesmo sendo dono ou moderador.',
            'Supporter — ativa ou desativa a peça de Supporter.',
            'Idioma — muda o idioma de toda a interface do chat.',
            'LinkValidator — desativa o aviso de segurança ao clicar em links externos.',
            'Palavras impróprias — oculta palavrões no chat (clique para revelar quando quiser).',
          ],
        },
        { type: 'heading', level: 2, text: 'Aparência' },
        {
          type: 'paragraph',
          text: 'Essas opções controlam como o chat é exibido. Algumas ajudam se o chat estiver travando:',
        },
        {
          type: 'list',
          items: [
            'Modo Noturno — fundo escuro para o chatbox, ideal para uso à noite.',
            'YouTube — escolha entre janela flutuante ou fixa para os vídeos.',
            'Animação — desativa todas as animações do chat.',
            'Animação de fundo — desativa backgrounds animados (PCBack, Pstyle).',
            'Flix / Hugs / Ultra Smiley — desativa efeitos visuais específicos de powers.',
            'Reações — desativa a exibição de reações nas mensagens.',
            'GIFs — escolha entre animado, sem animação, play on hover ou desativado.',
            'Autocompletar — desativa o autocompletar de smilies.',
          ],
        },
        { type: 'heading', level: 2, text: 'Notificações' },
        {
          type: 'paragraph',
          text: 'Configure alertas do xat mesmo quando você estiver em outra aba do navegador:',
        },
        {
          type: 'list',
          items: [
            'Notificações — receba alertas de todos os usuários ou só de amigos.',
            'Chat principal — notifique para cada mensagem enviada no chat.',
            'Menções — defina palavras-chave para receber alerta quando digitadas (ex: seu nome).',
          ],
        },
        { type: 'heading', level: 2, text: 'Macros — atalhos de texto' },
        {
          type: 'paragraph',
          text: 'Macros são mensagens prontas ativadas por atalhos. Crie um atalho e ao digitar $atalho na caixa, o xat substitui pelo texto completo automaticamente.',
        },
        {
          type: 'steps',
          items: [
            {
              title: 'Crie a macro',
              description:
                'No campo "Nome", escreva o atalho (ex: "regras"). No campo "Valor", escreva o texto completo. Clique em "Adicionar".',
            },
            {
              title: 'Use no chat',
              description:
                'Digite $regras na caixa de mensagem e pressione Enter. O xat envia o texto completo que você definiu.',
            },
            {
              title: 'Editar ou excluir',
              description:
                'Passe o mouse sobre a macro criada para ver os botões de editar (lápis) e excluir (X vermelho).',
            },
          ],
        },
        {
          type: 'tip',
          title: 'Macros são essenciais para moderadores',
          content:
            'Salve as regras do chat, links de suporte ou mensagens frequentes como macros. Um simples $regras e pronto — sem precisar redigitar tudo.',
        },
        { type: 'heading', level: 2, text: 'Sons e rádio' },
        {
          type: 'paragraph',
          text: 'Clique no ícone de rádio no canto inferior direito para gerenciar os sons. Uma janela com três controles abre: som das mensagens, som do chat e rádio. Clique para ligar/desligar. Para ajustar o volume, passe o mouse sobre a barra acima de cada ícone.',
        },
      ],
    },

    10: {
      intro:
        'Você consegue usar o xat sem registrar, mas criar uma conta gratuita muda tudo. De manter seu histórico a comprar powers — descubra por que vale tanto a pena.',
      blocks: [
        { type: 'heading', level: 2, text: 'Por que criar uma conta?' },
        {
          type: 'paragraph',
          text: 'Sem registro, você recebe um ID temporário que desaparece quando você sai. Com uma conta registrada, tudo que você conquista no xat fica salvo permanentemente:',
        },
        {
          type: 'list',
          items: [
            'Seu ID e nome ficam permanentes e acessíveis de qualquer dispositivo.',
            'Seus ranks (moderador, membro, dono) são mantidos nos chats.',
            'Sua lista de amigos fica salva e sincronizada em todos os dispositivos.',
            'Você pode comprar xats, days e powers.',
            'Pode receber presentes e transferências de outros usuários.',
            'Pode criar seu próprio xatspace (perfil pessoal do xat).',
            'Muito mais difícil de ser impersonado por outras pessoas.',
            'Seu nome aparece mais alto na lista de visitantes que os não registrados.',
            'Acessa recursos exclusivos: edição de mensagens, GIFs e reações.',
          ],
        },
        { type: 'heading', level: 2, text: 'Como criar sua conta' },
        {
          type: 'steps',
          items: [
            {
              title: 'Clique em "Registrar"',
              description: 'No canto superior direito do xat.',
            },
            {
              title: 'Preencha os dados',
              description:
                'Informe um e-mail válido e escolha uma senha forte. Use apenas letras e números — caracteres especiais são ignorados automaticamente.',
            },
            {
              title: 'Verifique seu e-mail',
              description: 'Um link de ativação será enviado. Clique nele para confirmar o cadastro.',
            },
            {
              title: 'Faça login',
              description:
                'Entre com seu nome e senha. Talvez seja necessário atualizar a página dos chats abertos para o registro entrar em efeito.',
            },
          ],
        },
        {
          type: 'tip',
          title: 'E-mail não chegou?',
          content:
            'Verifique a pasta de spam. Se ainda assim não chegar, tente registrar com uma conta Gmail.',
        },
        { type: 'heading', level: 2, text: 'Gerenciando sua conta' },
        {
          type: 'list',
          items: [
            'Trocar nome de usuário: Acesse xat.com/login > aba "alterar nome de usuário". Pode trocar uma vez a cada 7 dias. O nome antigo fica disponível para outros.',
            'Trocar senha: Acesse xat.com/login > aba "alterar senha". Informe a senha atual e a nova.',
            'Recuperar senha perdida: Acesse xat.com/lostpass, informe seu e-mail e aguarde o link (pode levar até 30 minutos).',
            'Trocar e-mail: Abra um ticket de suporte. Por segurança, só é possível trocar para e-mail Gmail.',
          ],
        },
        {
          type: 'warning',
          title: 'Dicas de segurança para a senha',
          content:
            'Use pelo menos 8 caracteres com letras maiúsculas, minúsculas e números. Evite dados pessoais como aniversário ou nome. Troque a senha a cada 3–6 meses. Caracteres especiais (!@#$) são ignorados automaticamente no login.',
        },
        { type: 'heading', level: 2, text: 'Boas práticas no chat' },
        {
          type: 'paragraph',
          text: 'Ao entrar em qualquer chat, você concorda com os Termos de Serviço do xat. Além das regras oficiais, alguns comportamentos são considerados má educação e podem resultar em ban:',
        },
        {
          type: 'list',
          items: [
            'Flood ou spam — enviar muitas mensagens seguidas ou textos sem sentido.',
            'CAPS LOCK excessivo — escrever tudo em maiúsculas é percebido como gritar.',
            'Excesso de smilies — muitos smilies num único envio pode travar o chat dos outros.',
            'Spam de audies — sons repetidos incomodam todos.',
            'Propaganda — promover seu chat em outros chats é proibido pelos Termos de Serviço.',
            'Pedir para ser staff — pedir rank sem merecê-lo demonstra imaturidade.',
            'Pedir xats/powers de graça — as pessoas trabalham para ter o que têm.',
          ],
        },
        {
          type: 'tip',
          title: 'Ganhar prêmios sem comprar',
          content:
            'Quer xats, days ou powers sem gastar dinheiro? Participe de concursos no fórum do xat (forum.xat.com) ou nos chats oficiais. É a forma mais divertida e legítima de ganhar.',
        },
      ],
    },
  },

  troubleshooting: {
    4: {
      intro:
        'O xat exibe códigos de erro quando algo impede login, registro, transferências, compras ou gestão de grupos. Esta página reúne os erros mais comuns, o que significam e o que fazer para resolvê-los — incluindo quando abrir um ticket de suporte.',
      blocks: [
        { type: 'heading', level: 2, text: 'Erros de gestão de conta' },
        {
          type: 'paragraph',
          text: 'Estes erros aparecem ao registrar, fazer login, alterar dados ou excluir a conta. A mensagem costuma começar com «E» seguida de um número.',
        },
        {
          type: 'table',
          headers: ['Código', 'Significado', 'O que fazer'],
          rows: [
            [
              'E4 / E50',
              'ID já registrado.',
              'Tente fazer login. Se não foi você quem registrou, obtenha um novo ID temporário e registre outra conta.',
            ],
            [
              'E5',
              'Termos não aceitos.',
              'Marque a caixa de concordância com os Termos de Serviço antes de continuar.',
            ],
            [
              'E6 / E19',
              'Nome de usuário já em uso.',
              'Escolha outro nome disponível.',
            ],
            [
              'E7',
              'E-mail já associado a outra conta.',
              'Use outro e-mail ou recupere a senha da conta existente em xat.com/lostpass.',
            ],
            [
              'E11 / E12 / E24 / E66',
              'Muitas tentativas falhadas.',
              'Aguarde 10 minutos e tente novamente.',
            ],
            [
              'E14 / E25',
              'Senha ou credenciais incorretas.',
              'Confirme usuário, e-mail e senha. Use xat.com/lostpass para recuperar o acesso.',
            ],
            [
              'E15 / E42',
              'E-mail inválido.',
              'Use um endereço no formato correto (ex.: [email protected]). Prefira Gmail.',
            ],
            [
              'E17',
              'E-mail não encontrado ou troca de nome bloqueada.',
              'Para e-mail: abra ticket em «Lost Access». Para nome: aguarde 7 dias entre cada alteração.',
            ],
            [
              'E20',
              'Não é possível excluir a conta.',
              'Aguarde 14 dias após registrar ou trocar o nome de usuário.',
            ],
            [
              'E22',
              'Confirmação por e-mail necessária.',
              'Verifique sua caixa de entrada e confirme a exclusão pelo link enviado.',
            ],
            [
              'E23',
              'Conta ainda possui xats, days ou powers.',
              'Transfira ou use todos os recursos antes de excluir a conta.',
            ],
            [
              'E26',
              'Conta não ativada.',
              'Contas novas: clique no link de ativação no e-mail. Contas antigas: abra ticket em «Account Block».',
            ],
            [
              'E28 / E55',
              'Conta excluída por violação dos Termos.',
              'Abra ticket em «Account Block» usando o e-mail associado à conta.',
            ],
            [
              'E29',
              'Conta bloqueada por localização (locked out).',
              'Abra ticket em «Locked Out» com dados para verificar que você é o titular.',
            ],
            [
              'E30 / E64',
              'Verificação de segurança por e-mail.',
              'Confira o e-mail da conta e use o PIN ou link enviado para concluir o login.',
            ],
            [
              'E31',
              'Proteção de conta não pode ser ativada.',
              'Só funciona na localização «casa». Abra ticket em «Location Update» se mudou de IP/país.',
            ],
            [
              'E39',
              'Senha muito curta.',
              'Use pelo menos 6 caracteres.',
            ],
            [
              'E40',
              'Senhas não coincidem.',
              'Digite a mesma senha nos dois campos.',
            ],
            [
              'E43',
              'Provedor de e-mail bloqueado.',
              'Registre-se com outro e-mail — de preferência Gmail.',
            ],
            [
              'E46 / E67',
              'ID inválido (k2).',
              'Abra ticket em «Account Block» com outra conta e informe o Username/ID.',
            ],
            [
              'E47',
              'Nome muito curto, longo ou com caracteres inválidos.',
              'O nome deve ter entre 10 e 18 caracteres válidos.',
            ],
            [
              'E48',
              'Nome não permitido.',
              'Evite palavras reservadas (ex.: «xat») ou termos impróprios.',
            ],
            [
              'E49',
              'Nome não pode começar com número.',
              'Escolha um nome que comece com letra.',
            ],
            [
              'E51',
              'ID já foi registrado ou excluído antes.',
              'Abra ticket em «Lost Access» informando o ID que tentou registrar.',
            ],
          ],
        },
        { type: 'heading', level: 2, text: 'Erros de sistema e bloqueios' },
        {
          type: 'paragraph',
          text: 'Aparecem como «System problem (número)» durante login, transferências, trades ou compras na loja.',
        },
        {
          type: 'table',
          headers: ['Código', 'Significado', 'O que fazer'],
          rows: [
            [
              '1',
              'Erro ao criar grupo.',
              'Complete o reCAPTCHA, aceite os Termos e tente novamente.',
            ],
            [
              '2 / 10',
              'Não é possível registrar este ID.',
              'Ative o ID no mesmo local onde foi registrado, ou obtenha um novo ID.',
            ],
            [
              '11',
              'Não é possível excluir conta (transfer block).',
              'Abra ticket em «Account Block».',
            ],
            [
              '12',
              'Conta em hold — não pode excluir ou trocar nome.',
              'Aguarde o fim do hold ou abra ticket em «Account Hold».',
            ],
            [
              '14',
              'Login em manutenção.',
              'Tente novamente mais tarde.',
            ],
            [
              '20 / 21',
              'Sessão expirada ou trade inválido.',
              'Atualize o chat (F5) ou faça login novamente.',
            ],
            [
              '23',
              'Destinatário usa power Mint.',
              'Peça para desativar Mint e reduzir o valor mínimo de transferência.',
            ],
            [
              '25',
              'Vários recursos desativados (ToS/segurança).',
              'Abra ticket em «Account Block».',
            ],
            [
              '26 / 56',
              'Destinatário com transfer block.',
              'O destinatário deve abrir ticket em «Account Block».',
            ],
            [
              '27',
              'Limite de days excedido.',
              'Transfira ou use days antes de receber mais, ou use DX.',
            ],
            [
              '28',
              'Transferência acima do limite.',
              'Máximo por transferência: 100.000 xats ou 9.000 days.',
            ],
            [
              '29',
              'Possível edição de trade por programa externo.',
              'Tire print, denuncie no chat e abra ticket em «Report Scam».',
            ],
            [
              '30',
              'Compra de powers bloqueada.',
              'Abra ticket em «Account Block».',
            ],
            [
              '31',
              'Promoção de chat bloqueada.',
              'Abra ticket em «Account Block».',
            ],
            [
              '32',
              'Todas as transações suspensas.',
              'Abra ticket em «Account Block».',
            ],
            [
              '33',
              'Hold temporário em transferências.',
              'Aguarde o prazo. Se não diminuir, abra ticket em «Account Hold».',
            ],
            [
              '40',
              'Trade em manutenção.',
              'Tente novamente mais tarde.',
            ],
            [
              '42',
              'Power não negociável.',
              'Tickle e alguns powers especiais não podem ser trocados.',
            ],
            [
              '43',
              'Troca 1 day por 1 day não permitida.',
              'Ajuste os valores do trade.',
            ],
            [
              '47',
              'Transferência para celebrity.',
              'Trades funcionam; transferências diretas de xats/days podem ser bloqueadas.',
            ],
            [
              '49',
              'Usuário offline.',
              'Aguarde o usuário voltar ao chat.',
            ],
            [
              '51',
              'Trade com bot ou usuário mobile.',
              'Use PC/navegador para concluir a troca.',
            ],
            [
              '55',
              'Conta held indefinidamente.',
              'Bloqueia trades e transferências. Abra ticket em «Account Block».',
            ],
            [
              '63',
              'Power Hug não reconhecido.',
              'Atualize o chat (F5) ou faça login novamente.',
            ],
            [
              '65',
              'Jinx no chat principal.',
              'Use /jinx e /jinxall apenas no chat privado do usuário.',
            ],
            [
              '66',
              'Recurso não suportado no mobile.',
              'Transferências, trades e casamentos/BFF exigem PC/navegador.',
            ],
            [
              '71',
              'Recurso não negociável (ex.: Aces).',
              'Aces e features similares não podem ser transferidos.',
            ],
            [
              '99',
              'Problema no servidor.',
              'Tente novamente mais tarde.',
            ],
          ],
        },
        { type: 'heading', level: 2, text: 'Erros de grupos e short names' },
        {
          type: 'table',
          headers: ['Código', 'Contexto', 'O que fazer'],
          rows: [
            [
              'Not available (1) / (11)',
              'Grupo ainda não inativo.',
              'O chat precisa ficar 3 meses sem staff para certas ações.',
            ],
            [
              'Not available (2) / (5) / (8)',
              'Grupo delistado ou excluído.',
              'Abra ticket em «Chat Block» para saber o motivo.',
            ],
            [
              'Not available (6)',
              'Grupo já comprado.',
              'Usuários pagos podem pedir reativação em «Short Names and Groups».',
            ],
            [
              'Not available (7)',
              'Grupo criado há menos de 6 meses.',
              'Aguarde o prazo exigido.',
            ],
            [
              'Not available (9)',
              'Grupo com ID baixo.',
              'Restrição específica desse grupo.',
            ],
            [
              'Not available (12)',
              'Nome reservado no grupo.',
              'Evite palavras como «xat» ou «chat» no nome.',
            ],
            [
              'Not available (13)',
              'Grupo não pode ser comprado.',
              'Escolha outro nome ou verifique disponibilidade.',
            ],
            [
              'Name taken (1)',
              'Short name em uso.',
              'Escolha outro nome.',
            ],
            [
              'Name taken (2)',
              'Short name excluído.',
              'Usuários pagos podem pedir reativação em «Short Names and Groups».',
            ],
            [
              'Not allowed',
              'Palavra reservada ou imprópria.',
              'Escolha um nome sem termos proibidos.',
            ],
            [
              'Erro (6) — grupo',
              'Nome de grupo indisponível.',
              'Se for usuário pago, abra ticket em «Short Names and Groups».',
            ],
          ],
        },
        { type: 'heading', level: 2, text: 'Erros do sistema de tickets' },
        {
          type: 'table',
          headers: ['Código', 'Significado', 'O que fazer'],
          rows: [
            [
              '403',
              'E-mail na lista de banimento de tickets.',
              'Contate um voluntário no chat Help ou no fórum xat.',
            ],
          ],
        },
        {
          type: 'info',
          title: 'Quando abrir um ticket',
          content:
            'Muitos erros de conta (E28, E29, E55, system 25, 55, etc.) só são resolvidos pela equipa xat via ticket. Consulte o guia «Sistema de tickets do xat» para saber qual tópico escolher e como preencher o pedido corretamente.',
        },
        {
          type: 'tip',
          title: 'Antes de pedir ajuda',
          content:
            'Anote o código exato do erro, o que você estava fazendo e — se for conta bloqueada — dados que comprovem que você é o titular (ID, e-mail, IP antigo, xats/days na conta, provedor de internet). Isso acelera a resposta do suporte.',
        },
      ],
    },

    5: {
      intro:
        'O ticket do xat é o sistema oficial de suporte para falar com voluntários e administradores sobre sua conta, pagamentos, bloqueios ou qualquer dúvida. Está disponível para todos os usuários registrados em xat.com/ticket.',
      blocks: [
        {
          type: 'info',
          title: 'Idioma dos tickets',
          content:
            'A interface do ticket aparece no idioma do seu navegador, mas o sistema de suporte funciona em inglês. Tickets e respostas devem ser enviados em inglês — você pode ser orientado a usar um tradutor. Escreva frases completas e claras para facilitar a tradução.',
        },
        {
          type: 'warning',
          title: 'Usuário pago',
          content:
            'Alguns tópicos exigem ser usuário pago (quem já comprou xats na página oficial de compra). Se não for pago, use o fórum xat (forum.xat.com) na secção General Support ou peça ajuda no chat Help.',
        },
        { type: 'heading', level: 2, text: 'Como criar um ticket' },
        {
          type: 'steps',
          items: [
            {
              title: 'Acesse o sistema',
              description:
                'Abra xat.com/ticket e clique para criar um novo ticket. É necessário estar logado — seu nome registrado aparecerá automaticamente no campo «xat username».',
            },
            {
              title: 'Confirme credenciais',
              description:
                'Informe a senha e o e-mail da conta correspondente. Se não conseguir logar, abra o ticket por uma conta alternativa (veja o guia de registro).',
            },
            {
              title: 'Escolha o tópico',
              description:
                'No campo «Help Topic», selecione o assunto mais próximo do seu problema na lista suspensa.',
            },
            {
              title: 'Defina assunto e mensagem',
              description:
                'Assunto: resumo curto (ex.: «Locked out of ID 12345678»). Mensagem: explique o problema com detalhes — ID, e-mail, IP antigo, xats/days, provedor, localização anterior, etc.',
            },
            {
              title: 'Envie e aguarde',
              description:
                'Clique em «Open Ticket». Você receberá e-mail quando houver resposta. Pode responder ao próprio ticket se lembrar de informações extras.',
            },
          ],
        },
        {
          type: 'info',
          title: 'Um ticket por vez',
          content:
            'Só é permitido um ticket aberto por conta. Se precisar de outro, aguarde resposta do voluntário (a resposta fecha o ticket) ou peça a um voluntário para abrir um novo.',
        },
        { type: 'heading', level: 2, text: 'Tópicos de ajuda (Help Topics)' },
        {
          type: 'paragraph',
          text: 'Escolha o tópico correto — tickets no tópico errado podem demorar mais ou não ser respondidos.',
        },
        {
          type: 'table',
          headers: ['Tópico', 'Quando usar'],
          rows: [
            [
              'A General Question or Concern',
              'Dúvidas gerais ou quando não souber qual secção usar.',
            ],
            [
              'Account Block',
              'Erro 25/55 ao tradear/transferir, conta held mais de 7 dias, conta ou xatspace excluídos.',
            ],
            [
              'Account Hold',
              'Conta em hold há menos de 7 dias.',
            ],
            [
              'Chat Block',
              'Chat bloqueado de promoção ou com conteúdo removido.',
            ],
            [
              'Domain Unblock',
              'Desbloquear domínio no LinkValidator ou abaixo do chat.',
            ],
            [
              'Email Change / Hint',
              'Trocar e-mail de conta que você ainda acessa (apenas Gmail, por segurança).',
            ],
            [
              'Location Update',
              'Atualizar localização ou IP para usar Account Protection.',
            ],
            [
              'Locked Out',
              'Bloqueado fora da conta por mudança de localização (erro E29).',
            ],
            [
              'Lost Access',
              'Esqueceu e-mail e senha, ou senha sem acesso ao e-mail.',
            ],
            [
              'Official Chats',
              'Gestores de chats oficiais que precisam falar com o xat.',
            ],
            [
              'Payment Problems',
              'Xats/days não creditados após compra, problemas de reserve, etc.',
            ],
            [
              'Report Phishing Site',
              'Sites de phishing, servidores privados ou sites contra os Termos.',
            ],
            [
              'Report Scam',
              'Foi scammado, phished ou faltam xats — inclua provas (prints).',
            ],
            [
              'Short Names and Groups',
              'Transferir short name ou recolocar nome/grupo à venda.',
            ],
            [
              'Supporter and Celebrity Requests',
              'Pedido de Supporter ou Celebrity (inclua links de redes sociais).',
            ],
            [
              'xat chat / site issues',
              'Bugs no chat, site ou serviços — descreva passos para reproduzir o erro.',
            ],
          ],
        },
        {
          type: 'tip',
          title: 'Tempos de resposta',
          content:
            'Alguns departamentos demoram mais que outros. Consulte o artigo «Ticket times» na wiki xat para ver o estado atual de cada secção.',
        },
        { type: 'heading', level: 2, text: 'Ver e responder a um ticket aberto' },
        {
          type: 'steps',
          items: [
            {
              title: 'Check Status',
              description:
                'Em xat.com/ticket, clique em «Check Status». Informe sua senha xat quando solicitado.',
            },
            {
              title: 'Selecione o ticket',
              description:
                'Aparecerá a lista de tickets existentes na conta. Clique no que deseja consultar.',
            },
            {
              title: 'Responda ou encerre',
              description:
                'Se o problema continuar, responda com mais detalhes. Se estiver resolvido, não é necessário responder — o ticket fica concluído.',
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'Outra forma: ao receber e-mail do xat sobre uma resposta, clique no link incluído. Use o e-mail associado à conta que abriu o ticket.',
        },
        {
          type: 'warning',
          title: 'Payment Problems',
          content:
            'Use «Payment Problems» apenas para problemas de pagamento (xats/days em falta, reserve, etc.). Outros assuntos nesse tópico não serão respondidos — use «A General Question or Concern» se tiver dúvida.',
        },
      ],
    },
  },
};
